import React, { useEffect, useState } from "react";
import {
  X,
  Package,
  Boxes,
  Calendar,
  Layers,
  Building2,
  Receipt,
  Tag,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { ProductBatch } from "../../../models/productOutwardBatches.model";
import { MaterialBatch } from "../../../models/materialOutwardBatches.model";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import { getRequestOutwardBatches } from "../../../store/slices/Factory/newFloorSlice";
import LoadingSpinner from "../../../components/AssetsComponent/LoadingSpinner";

interface BatchesViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "product" | "material";
  requestId: string;
}

export const BatchesViewModal: React.FC<BatchesViewModalProps> = ({
  isOpen,
  onClose,
  type,
  requestId,
}) => {
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>(
    {},
  );
  const dispatch: RootDispatch = useDispatch();

  const productBatches = useSelector(
    (state: RootState) => state.newFloor.productOutwardBatches,
  );
  const materialBatches = useSelector(
    (state: RootState) => state.newFloor.materialOutwardBatches,
  );
  const loading = useSelector(
    (state: RootState) =>
      state.newFloor.loading.getRequestOutwardBatchesLoading,
  );
  useEffect(() => {
    if (isOpen) {
      // Fetch the outward batches for the given requestId
      dispatch(getRequestOutwardBatches({ requestId }));
    }
  }, [dispatch, isOpen, requestId]);

  if (!isOpen) return null;
  const toggleExpand = (id: string) => {
    setExpandedCards((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-3 sm:p-6 transition-all duration-300">
      {loading && <LoadingSpinner />}
      <div className="relative flex flex-col w-full max-w-5xl max-h-[90vh] bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
              {type === "product" ? (
                <Package className="w-5 h-5" />
              ) : (
                <Boxes className="w-5 h-5" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                {type === "product" ? "Product Batches" : "Material Batches"}
              </h2>
              <p className="text-xs text-slate-500">
                Detailed view of dispatched items and material allocations
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 bg-slate-50/50">
          {type === "product" && productBatches ? (
            productBatches.length === 0 ? (
              <EmptyState message="No product batches found" />
            ) : (
              productBatches.map((batch) => (
                <ProductBatchCard
                  key={batch._id}
                  batch={batch}
                  isExpanded={!!expandedCards[batch._id]}
                  onToggle={() => toggleExpand(batch._id)}
                />
              ))
            )
          ) : materialBatches?.length === 0 ? (
            <EmptyState message="No material batches found" />
          ) : (
            materialBatches?.map((batch) => (
              <MaterialBatchCard key={batch._id} batch={batch} />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-white">
          <span className="text-xs text-slate-400">
            Showing{" "}
            {type === "product"
              ? productBatches?.length
              : materialBatches?.length}{" "}
            entries
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/* --- PRODUCT BATCH CARD SUB-COMPONENT --- */
const ProductBatchCard: React.FC<{
  batch: ProductBatch;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ batch, isExpanded, onToggle }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Primary Card Header */}
      <div className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-bold text-slate-900 text-base">
                {batch.productId?.productName || "Unnamed Product"}
              </span>
              {batch.productId?.sku && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-600">
                  <Tag className="w-3 h-3 mr-1" />
                  {batch.productId.sku}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-2">
              <span>
                S/N:{" "}
                <strong className="text-slate-700">{batch.serialNumber}</strong>
              </span>
              <span>•</span>
              <span>
                Logger:{" "}
                <strong className="text-slate-700">
                  {batch.loggerSerialNumber}
                </strong>
              </span>
            </p>
          </div>

          <div className="text-right">
            <span className="text-xs text-slate-400 block">
              Total Unit Cost
            </span>
            <span className="text-lg font-extrabold text-indigo-600">
              ₹{batch.totalCost.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Quick Details Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 px-3.5 bg-slate-50/80 rounded-lg text-xs">
          <div>
            <span className="text-slate-400 block">Warehouse</span>
            <span className="font-semibold text-slate-700 flex items-center gap-1 mt-0.5">
              <Building2 className="w-3.5 h-3.5 text-slate-400" />
              {batch.warehouseId?.name || "N/A"}
            </span>
          </div>
          <div>
            <span className="text-slate-400 block">Batch No</span>
            <span className="font-semibold text-slate-700 flex items-center gap-1 mt-0.5">
              <Layers className="w-3.5 h-3.5 text-slate-400" />#
              {batch.requestId?.batchNo ?? "N/A"}
            </span>
          </div>
          <div>
            <span className="text-slate-400 block">Labor Cost</span>
            <span className="font-semibold text-slate-700 mt-0.5 block">
              ₹{batch.labourCost}
            </span>
          </div>
          <div>
            <span className="text-slate-400 block">Dispatched Date</span>
            <span className="font-semibold text-slate-700 mt-0.5 block">
              {batch.dispatchedDate
                ? new Date(batch.dispatchedDate).toLocaleDateString()
                : "Not Dispatched"}
            </span>
          </div>
        </div>

        {/* Accordion Toggle for Materials Used */}
        <button
          onClick={onToggle}
          className="w-full mt-3 flex items-center justify-between text-xs font-semibold text-indigo-600 hover:text-indigo-700 pt-2 border-t border-slate-100"
        >
          <span>
            {isExpanded ? "Hide" : "View"} Materials Used (
            {batch.materialsUsed?.length || 0})
          </span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Expanded Materials Used Details */}
      {isExpanded && (
        <div className="bg-slate-50/80 border-t border-slate-100 p-4 space-y-3">
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Consumed Raw Materials
          </h4>

          <div className="space-y-2">
            {batch.materialsUsed?.map((mat) => (
              <div
                key={mat._id}
                className="flex flex-col sm:flex-row sm:items-center justify-between bg-white p-3 rounded-lg border border-slate-200/60 text-xs gap-2"
              >
                <div>
                  <span className="font-semibold text-slate-800 block">
                    {mat.rawMaterialId?.name}
                  </span>
                  <div className="flex gap-3 text-slate-400 text-[11px] mt-0.5">
                    <span>SKU: {mat.rawMaterialId?.sku || "N/A"}</span>
                    <span>Bill: {mat.storeInwardId?.billNumber || "N/A"}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-4 text-slate-600 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                  <div className="text-left sm:text-right">
                    <span className="text-slate-400 text-[10px] block">
                      Qty Used
                    </span>
                    <span className="font-bold text-slate-700">
                      {mat.quantityUsed} {mat.rawMaterialId?.unit || ""}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-slate-400 text-[10px] block">
                      Cost
                    </span>
                    <span className="font-bold text-slate-900">
                      ₹{mat.totalCost}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* --- MATERIAL BATCH CARD SUB-COMPONENT --- */
const MaterialBatchCard: React.FC<{ batch: MaterialBatch }> = ({ batch }) => {
  return (
    <div className="bg-white p-4 sm:p-5 rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <span className="font-bold text-slate-900 text-base block">
            {batch.productId?.name || "Raw Material"}
          </span>
          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
            {batch.productId?.sku && (
              <span className="px-2 py-0.5 rounded-md bg-slate-100 font-medium">
                SKU: {batch.productId.sku}
              </span>
            )}
            {batch.billNumber && (
              <span className="flex items-center gap-1 text-slate-600">
                <Receipt className="w-3.5 h-3.5" />
                Bill: {batch.billNumber}
              </span>
            )}
          </div>
        </div>

        <div className="text-right">
          <span className="text-xs text-slate-400 block">Total Price</span>
          <span className="text-lg font-extrabold text-emerald-600">
            ₹{(batch.totalPrice || 0).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 px-3.5 bg-slate-50/80 rounded-lg text-xs">
        <div>
          <span className="text-slate-400 block">Quantity</span>
          <span className="font-bold text-slate-800 mt-0.5 block">
            {batch.quantity} {batch.productId?.unit}
          </span>
        </div>
        <div>
          <span className="text-slate-400 block">Used in Production</span>
          <span className="font-semibold text-slate-700 mt-0.5 block">
            {batch.quantityUsedInProduction} {batch.productId?.unit}
          </span>
        </div>
        <div>
          <span className="text-slate-400 block">Vendor</span>
          <span className="font-semibold text-slate-700 mt-0.5 block truncate">
            {batch.vendorId?.name || "N/A"}
          </span>
        </div>
        <div>
          <span className="text-slate-400 block">Received Date</span>
          <span className="font-semibold text-slate-700 mt-0.5 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            {batch.receivedDate
              ? new Date(batch.receivedDate).toLocaleDateString()
              : "N/A"}
          </span>
        </div>
      </div>
    </div>
  );
};

/* --- EMPTY STATE SUB-COMPONENT --- */
const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center bg-white rounded-xl border border-dashed border-slate-200">
    <div className="p-3 bg-slate-100 rounded-full text-slate-400 mb-3">
      <Package className="w-6 h-6" />
    </div>
    <p className="text-sm font-semibold text-slate-600">{message}</p>
    <p className="text-xs text-slate-400 mt-1">
      No batch items found for this request.
    </p>
  </div>
);
